import React, { useState, useLayoutEffect, useRef } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useAnnouncements } from './AnnouncementAPIEndpoints';
import { UcPagination } from 'components/common/UcPagination';
import { useTranslation } from "react-i18next";
import { UcIconButtons } from 'components/common/UcIconButtons';
import { getWholeRow } from '../../../lib/DataGridHelpers';
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete';
import { dateFormatter } from '../../../lib/Formatters';
import { Box, Chip } from '@mui/material';

export const AnnouncementGrid = (props) => {
    const { t } = useTranslation();
    const [generalSearchInput] = useState(props.generalSearch || "");
    const initialSort = [{ field: "createdTs", sort: "desc" }];
    const sort = props.sort || initialSort;

    const initialDataState = props.dataState || { skip: 0, take: 10, };

    const [filter, setFilter] = useState({
        take: initialDataState.take,
        skip: initialDataState.skip,
        sort: sort,
        generalSearch: generalSearchInput,
        // roleType: 'custom' // TODO: Decide if we only want to show the new Custom Announcement Type here.
    });

    let initialFilterModel = {
        items: [
            { columnField: 'header', operatorValue: 'contains', value: '' }
        ],
        linkOperator: 'and',
    };

    if (generalSearchInput) {
        initialFilterModel = {
            items: [
                { columnField: 'header', operatorValue: 'contains', value: generalSearchInput }
            ],
            linkOperator: 'and',
        }
    }

    const [filterModel] = useState(initialFilterModel);

    const needsDataUpdate = useRef("");

    let defaultDataItems = {
        columnVisibilityModel: {
            id: true, // has buttons
            cepOrgId: false,
            content: false,
            createdBy: false,
            updatedBy: false,
        },
        columns: [
            { field: 'id', headerName: t('Id') },
            { field: 'cepOrgId', headerName: t('CEP Org ID'), width: 200 },
            { field: 'header', headerName: t('Header'), width: 200 },
            { field: 'content', headerName: t('Content'), width: 200 },
            { field: 'isPublished', headerName: t('Status'), width: 120,
                renderCell: (params) => {
                return (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="100vh"
                    >
                        <Chip
                            label={params.value ? t("Published") : t("Draft")}
                            color={params.value ? "info" : "warning"}
                            variant="outlined"
                        />
                    </Box>
                    );
                }
            },
            { field: 'createdBy', headerName: t('Created By'), width: 200 },
            { field: "createdTs", headerName: t('Created On'), width: 200, valueFormatter: (params) => { return dateFormatter(params.value); } },
            { field: 'updatedBy', headerName: t('Updated By'), width: 200 },
            { field: "updatedTs", headerName: t('Updated On'), width: 200, valueFormatter: (params) => { return dateFormatter(params.value); } },
            {
                field: 'emailsSent', headerName: t('Email Sent'), width: 200, renderCell: (params) => {
                    return (
                        <>
                            {params.value > 0 && (
                                <Chip
                                    label={t("EMAIL SENT")}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}                          
                        </>
                    );
                }
            },
            {
                field: 'isPinned', headerName: t('Pinned'), width: 120,
                renderCell: (params) => {
                    return (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            minHeight="100vh"
                        >
                            {params.value && (
                                <Chip
                                    label={t("Pinned")}
                                    color={"info"}
                                    variant="outlined"
                                />
                            )}
                        </Box>
                    );
                }
            },
            {
                field: 'id', headerName: '',
                valueGetter: getWholeRow,
                renderCell: (params) => {
                    const actions = [
                        {
                            title: t('Edit Announcement'),
                            icon: <EditIcon EditIcon data-cy-id={"announcement_edit_" + params.row.header}/>,
                            color: 'primary',
                            isHidden: false,
                            isDisabled: false,
                            requiredPermission: 'Announcement:Modify',
                            onClick: () => { props.onEditItem(params.value); },
                        },
                        {
                            title: t('Delete Announcement'),
                            icon: <DeleteIcon data-cy-id={"announcement_delete_" + params.row.header}/>,
                            color: 'error',
                            isHidden: false,
                            isDisabled: false,
                            requiredPermission: 'Announcement:Delete',
                            onClick: () => { props.onDeleteItem(params.value); },
                        }
                    ];

                    return (
                        <UcIconButtons actions={actions} />
                    );
                }
            },
        ],
        rows: [],
        rowCount: -1,
    };
    const [dataItems, setDataItems] = useState(defaultDataItems);

    const defaultSelectedItem = { dataItem: {}, selectedId: 0 };
    const [selectedItem, setSelectedItem] = useState(defaultSelectedItem);

    const isDataLoaded = useRef(false);

    const dataReceived = (swrResult) => {
        var dataItems = swrResult.result;

        var selectedDataItems = dataItems.map((item) => {
            return { ...item, selected: item.id === selectedItem.selectedId };
        });

        selectedDataItems.iteration = dataItems.iteration;
        selectedDataItems.rowCount = swrResult.rowCount;

        if (selectedDataItems.length > selectedDataItems.rowCount) {
            selectedDataItems.rowCount = selectedDataItems.length;
        }

        setDataItems({ ...defaultDataItems, rows: [...dataItems], rowCount: swrResult.rowCount });
    };

    const swrAnnouncements = useAnnouncements(filter); //Pass the datastate directly as the filter.  The API will adjust filters, orderbys ect.
    if (!swrAnnouncements.result && defaultDataItems.rowCount !== -1) {
        setDataItems(defaultDataItems);
    }

    useLayoutEffect(() => {
        if (swrAnnouncements.result) {
            isDataLoaded.current = true;
            var filterString = JSON.stringify(filter);
            if (needsDataUpdate.current !== filterString) {
                needsDataUpdate.current = filterString;
                dataReceived(swrAnnouncements);
            }
        }
    });

    const updateFilter = (filter) => {
        setFilter(filter);
        props.onFilterChange(filter);
    };

    return (
        <div
            data-cy-id={"announcementGridWrapper"}
            onDoubleClick={props.doubleClick}
        >
            <DataGridPro
                autoHeight={true}
                disableColumnFilter={true}
                disableColumnSelector={true}
                loading={!isDataLoaded.current}
                paginationModel={{page: filter.skip / filter.take, pageSize: filter.take}}
                onPaginationModelChange={(model) => {
                    updateFilter({ ...filter, skip: model.page * model.pageSize, take: model.pageSize })
                }}
                sortingMode='server'
                paginationMode='server'
                filterMode='server'
                sortModel={filter.sort}
                onSortModelChange={(model) => updateFilter({ ...filter, sort: model })}
                filterModel={filterModel}
                rowsPerPageOptions={[5, 10, 20]}
                onRowSelectionModelChange={(newSelectionModel) => {
                    if (newSelectionModel.length !== 1) {
                        setSelectedItem(defaultSelectedItem);
                        props.onSelected(defaultSelectedItem.dataItem);
                        return;
                    }
                    const selectedId = newSelectionModel[0];
                    var selectedItem = dataItems.rows.filter(obj => { return obj.id === selectedId });
                    if (selectedItem.length !== 1) {
                        setSelectedItem(defaultSelectedItem);
                        props.onSelected(defaultSelectedItem.dataItem);
                        return;
                    }
                    selectedItem = selectedItem[0];
                    var newlySelectedItem = { dataItem: selectedItem, selectedId: selectedId };

                    setSelectedItem(newlySelectedItem);
                    props.onSelected(newlySelectedItem.dataItem);
                }}
                pagination
                components={{
                    Toolbar: () => {return  props.toolbar || <></>},
                    Pagination: UcPagination,
                }}
                {...dataItems}
                initialState={{ pinnedColumns: { right: ['id'] } }}
            />
        </div>
    );
}

AnnouncementGrid.defaultProps = {
    onSelected: (selectedItem) => { console.log("onSelected not implemented", selectedItem); },
    onFilterChange: (filter) => { console.log("onFilterChange not implemented", filter); },
};
